/**
 * 로그인(EA-01) → 처음 설정(EA-03, FirstSetup.tsx) → PIN(EA-04).
 *
 * 정할 것 39(사장님 2026-10-13 「관리자 웹에 직원 OTP가 왜 있냐」 「직원 아이디 비밀번호만 있으면되고」, PIN 은 「1」 —
 * 그대로): 로그인 뒤 OTP 코드 입력 화면(EA-02)과 그 창구(XM-02)를 없앴다. 관리자 로그인 = 아이디 → 비밀번호 → PIN.
 *
 * 정할 것 45(사장님 45번 「첫로그인 이후 다른 직원이 같은계정으로 로그인 시도시 이미로그인중입니다. 로그아웃 후 접속하시겠습니까
 * 팝업뛰우고 예 누르면 이전세션 로그아웃 시키고 접속하면 1계정만 로그인되는거잖아」): 409 `already_signed_in` 이면 팝업
 * 「이미 로그인 중입니다. 로그아웃 후 접속하시겠습니까?」(서버 글 — 영어) → [Yes] 같은 아이디·비밀번호에 `force` 로 다시 XM-01
 * (서버가 앞 세션을 끝냄 — 한 계정 한 세션), [No] 로그인하지 않음.
 *
 * ⚠ **토큰이 생긴 뒤의 다음 단계는 App 이 정한다.** XM-06 의 `setupRequired`·
 * `pinVerified` 가 「처음 설정을 그릴까」「키패드를 그릴까」의 유일한 근거다 — 로그인
 * 답의 `setupRequired`·`pinRequired` 는 그 순간의 값일 뿐이고, 세션 도중에도 서버가
 * PIN·처음 설정을 다시 요구할 수 있다(명세 2-1).
 */
import { useState } from 'react'
import { login, verifyPin } from '@/api/admin'
import { ApiError, errorMessage } from '@/api/errors'
import { useSession } from '@/auth/session'
import { takeWrite, setFlash, forgetWrite } from '@/auth/pendingWrite'
import { Button, Card, Field, Notice } from '@/components/ui'
import { Confirm } from '@/components/Confirm'

/** 정할 것 45 팝업의 물음 — 서버 글이 없을 때(계약 XM-01 의 글과 같음). */
const ALREADY_SIGNED_IN = 'You are already signed in. Sign out of the other session and sign in here?'

export function SignIn() {
  const { signIn, signOutNotice } = useSession()
  const [loginId, setLoginId] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** 정할 것 45 — 이미 로그인 중이라 묻는 중이면 그 물음. */
  const [ask, setAsk] = useState<string | null>(null)

  async function send(force: boolean) {
    setBusy(true); setError(null)
    try {
      const r = force ? await login(loginId, password, true) : await login(loginId, password)
      setAsk(null)
      if (r.token) signIn(r.token)
    } catch (err) {
      if (!force && err instanceof ApiError && err.code === 'already_signed_in') {
        setAsk(err.serverMessage ?? ALREADY_SIGNED_IN)
        return
      }
      setAsk(null)
      // 레거시의 「(남은 시도 N회)」 는 계약에 칸이 없어 뺀다(명세 7절 C-01).
      setError(errorMessage(err, 'login'))
    } finally { setBusy(false) }
  }

  function submitPassword(e: React.FormEvent) {
    e.preventDefault()
    void send(false)
  }

  return (
    <div className="mx-auto mt-24 w-full max-w-sm">
      <h1 className="mb-6 text-center text-xl font-semibold">SJO Exchange Admin</h1>
      <Card>
        {/* 로그아웃된 까닭(정할 것 21·34·37 최종 잠금 — 서버 글)은 로그인을 누르기 전까지만 — 누르면 그 결과를 보인다. */}
        {(error ?? signOutNotice) && <div className="mb-4"><Notice tone="stop">{error ?? signOutNotice}</Notice></div>}

        <form noValidate onSubmit={submitPassword} className="flex flex-col gap-4">
          <Field label="ID" value={loginId} autoComplete="username"
            onChange={(e) => setLoginId(e.target.value)} />
          <Field label="Password" type="password" value={password} autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)} />
          <Button type="submit" disabled={busy || !loginId || !password}>Sign in</Button>
        </form>
      </Card>
      {/* [No] 는 아무것도 하지 않는다 — 로그인하지 않음(앞 세션 그대로). */}
      <Confirm open={ask !== null} title="Already signed in" action="Yes" cancel="No" busy={busy} error={null}
        onCancel={() => setAsk(null)} onConfirm={() => { void send(true) }}>
        {ask}
      </Confirm>
    </div>
  )
}

/**
 * PIN 관문(EA-04).
 *
 * ⚠ **로그인 화면이 아니라 앱 차원의 관문이다.** 서버가 어느 요청에서든 403
 * `pin_required` 를 줄 수 있다. 로그인 화면 안에 두면 이미 로그인한 사람이 그
 * 화면으로 못 돌아가고, 메뉴는 보이는데 아무것도 못 여는 상태에 갇힌다.
 */
export function PinGate() {
  const { refresh, signOut } = useSession()
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      await verifyPin(pin)
      await refresh()

      // ⚠ **PIN 때문에 멈췄던 쓰기를 여기서 한 번 보낸다.** 화면이 통째로
      // 갈아치워졌으므로 그 버튼은 이미 사라졌고, 보내려던 내용도 함께
      // 사라졌다 — 사람이 처음부터 다시 하지 않게 하는 것이 이 몇 줄이다.
      //
      // ⚠ **꺼내면서 지운다.** 남겨 두면 다음에 PIN 을 풀 때 하지도 않은 일이
      // 또 나간다. 승인은 되돌릴 수 없다.
      const w = takeWrite()
      if (w) {
        try {
          await w.run()
        } catch (e) {
          setFlash({
            tone: 'stop',
            text: `Could not send "${w.label}" again. Please try again on the screen.`,
          })
          void e
        }
      }
    } catch (err) {
      setError(errorMessage(err, 'pin_gate'))
      setPin('')
      // PIN 이 아직 없다 — 처음 설정이 남았을 수 있다. XM-06 이 EA-03 으로 보낸다.
      if (err instanceof ApiError && err.code === 'pin_not_set') void refresh()
    } finally { setBusy(false) }
  }

  return (
    <div className="mx-auto mt-24 w-full max-w-sm">
      <h1 className="mb-6 text-center text-xl font-semibold">SJO Exchange Admin</h1>
      <Card>
        {error && <div className="mb-4"><Notice tone="stop">{error}</Notice></div>}
        <form noValidate onSubmit={submit} className="flex flex-col gap-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">Enter your 6-digit admin PIN.</p>
          <Field label="PIN" type="password" value={pin} inputMode="numeric" maxLength={6}
            autoFocus onChange={(e) => setPin(e.target.value)} />
          <Button type="submit" disabled={busy || pin.length !== 6}>Confirm</Button>
          <button type="button" onClick={() => { forgetWrite(); signOut() }}
            className="text-xs text-slate-500 underline">Sign in with another account</button>
        </form>
      </Card>
    </div>
  )
}
